import React from "react";
import {CheckCircleRounded} from "@mui/icons-material";
import type {CoverageKpis as Kpis} from "../contractCoverageData";
import type {CoverageFilterKey} from "../contractCoverageUtils";
import {formatCompactCurrency, formatNumber} from "../contractCoverageUtils";

type Props = {
    kpis: Kpis;
    activeFilter: CoverageFilterKey | null;
    onToggleFilter: (key: CoverageFilterKey) => void;
};

const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

export const CoverageKpis: React.FC<Props> = ({kpis: k, activeFilter, onToggleFilter}) => {
    const cur = k.currency;
    return (
        <div className="cci-kpis">
            {/* Verdict cards — suppliers + spend, each out of the portfolio total */}
            <div className="cci-kpi-row cci-kpi-verdicts">
                <ContractCard
                    tone="required"
                    label="Required contracts"
                    active={activeFilter === 'required'}
                    onClick={() => onToggleFilter('required')}
                    suppliers={{value: k.contractRequired, total: k.suppliersAnalysed}}
                    spend={{value: k.requiredSpend, total: k.totalSpend, currency: cur}}
                />
                <ContractCard
                    tone="recommended"
                    label="Recommended contracts"
                    active={activeFilter === 'recommended'}
                    onClick={() => onToggleFilter('recommended')}
                    suppliers={{value: k.contractRecommended, total: k.suppliersAnalysed}}
                    spend={{value: k.recommendedSpend, total: k.totalSpend, currency: cur}}
                />
            </div>

            {/* Opportunity + portfolio cards */}
            <div className="cci-kpi-row cci-kpi-metrics">
                <SimpleCard
                    tone="saving" label="Cost saving opportunity"
                    value={formatCompactCurrency(k.costSavingEur, cur)}
                    active={activeFilter === 'saving'} onClick={() => onToggleFilter('saving')}
                />
                <SimpleCard
                    tone="avoidance" label="Cost avoidance"
                    value={formatCompactCurrency(k.costAvoidanceEur, cur)}
                    active={activeFilter === 'avoidance'} onClick={() => onToggleFilter('avoidance')}
                />
                <SimpleCard
                    tone="above" label="Above contracted value"
                    value={formatNumber(k.aboveContracted)} hint="suppliers"
                    active={activeFilter === 'above'} onClick={() => onToggleFilter('above')}
                />
                <SimpleCard
                    tone="neutral" label="Spend analysed"
                    value={formatCompactCurrency(k.totalSpend, cur)}
                    hint={`${formatNumber(k.suppliersAnalysed)} suppliers`}
                />
            </div>
        </div>
    );
};

type Metric = {value: number; total: number; currency?: string};

const ContractCard: React.FC<{
    tone: string;
    label: string;
    active: boolean;
    onClick: () => void;
    suppliers: Metric;
    spend: Metric;
}> = ({tone, label, active, onClick, suppliers, spend}) => (
    <button
        type="button"
        className={`cci-kpi cci-kpi-card tone-${tone}${active ? ' is-active' : ''}`}
        aria-pressed={active}
        onClick={onClick}>
        <span className="cci-kpi-head">
            <span className="cci-kpi-dot"/>
            {label}
            {active
                ? <CheckCircleRounded className="cci-kpi-check" fontSize="inherit"/>
                : <span className="cci-kpi-filterhint">Filter</span>}
        </span>
        <div className="cci-kpi-pair">
            <MetricCol caption="Suppliers"
                       value={formatNumber(suppliers.value)}
                       sub={`${fmtPct(pct(suppliers.value, suppliers.total))} · of ${formatNumber(suppliers.total)}`}
                       barPct={pct(suppliers.value, suppliers.total)}/>
            <MetricCol caption="Spend"
                       value={formatCompactCurrency(spend.value, spend.currency!)}
                       sub={`${fmtPct(pct(spend.value, spend.total))} · of ${formatCompactCurrency(spend.total, spend.currency!)}`}
                       barPct={pct(spend.value, spend.total)}/>
        </div>
    </button>
);

const MetricCol: React.FC<{caption: string; value: string; sub: string; barPct: number}> = ({caption, value, sub, barPct}) => (
    <div className="cci-kpi-metric">
        <span className="cci-kpi-metric-cap">{caption}</span>
        <span className="cci-kpi-metric-val">{value}</span>
        <span className="cci-kpi-metric-sub">{sub}</span>
        <span className="cci-kpi-bar"><span style={{width: `${Math.min(100, barPct)}%`}}/></span>
    </div>
);

const SimpleCard: React.FC<{
    tone: string;
    label: string;
    value: string;
    hint?: string;
    active?: boolean;
    onClick?: () => void;
}> = ({tone, label, value, hint, active, onClick}) => {
    const filterable = !!onClick;
    return (
        <button
            type="button"
            className={`cci-kpi cci-kpi-card cci-kpi-simple tone-${tone}${active ? ' is-active' : ''}${filterable ? '' : ' is-static'}`}
            aria-pressed={filterable ? !!active : undefined}
            disabled={!filterable}
            onClick={onClick}>
            <span className="cci-kpi-head">
                {label}
                {filterable && (active
                    ? <CheckCircleRounded className="cci-kpi-check" fontSize="inherit"/>
                    : <span className="cci-kpi-filterhint">Filter</span>)}
            </span>
            <span className="cci-kpi-value">{value}</span>
            {hint && <span className="cci-kpi-hint">{hint}</span>}
        </button>
    );
};
